// Localização: frontend/src/pages/Assinatura/SucessoPage.jsx
// Página de sucesso após assinatura

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader2, ArrowRight } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { getAssinatura } from '../../api/assinaturaApi';
import PrimaryButton from '../../components/Common/PrimaryButton';

const SucessoPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { fetchSubscription, subscription } = useAuth();
    const [loading, setLoading] = useState(true);
    const [countdown, setCountdown] = useState(5);

    useEffect(() => {
        // Buscar assinatura atualizada do backend
        const atualizarAssinatura = async () => {
            try {
                // Aguardar um pouco para garantir que o backend processou o pagamento
                await new Promise(resolve => setTimeout(resolve, 1000));
                
                // Buscar assinatura
                const response = await getAssinatura();
                if (response.success && response.data) {
                    const assinaturaData = response.data;
                    console.log('[SucessoPage] Assinatura encontrada:', assinaturaData);
                }
                
                // Atualizar assinatura no contexto - tentar múltiplas vezes se necessário
                let tentativas = 0;
                const maxTentativas = 5;
                while (tentativas < maxTentativas) {
                    await fetchSubscription();
                    // Verificar se a assinatura foi carregada e está ativa
                    if (subscription && subscription.status === 'ATIVO') {
                        console.log('[SucessoPage] Assinatura ATIVA detectada:', subscription);
                        break;
                    }
                    tentativas++;
                    if (tentativas < maxTentativas) {
                        await new Promise(resolve => setTimeout(resolve, 1500));
                    }
                }
            } catch (error) {
                console.error('[SucessoPage] Erro ao buscar assinatura:', error);
            } finally {
                setLoading(false);
            }
        };

        atualizarAssinatura();

        // Contador regressivo para redirecionamento automático
        const timer = setInterval(() => {
            setCountdown((prev) => {
                if (prev <= 1) {
                    clearInterval(timer);
                    navigate('/dashboard');
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [fetchSubscription, navigate, subscription]);

    const subscriptionId = searchParams.get('subscription_id');
    const status = searchParams.get('status');
    const isUpgrade = searchParams.get('upgrade') === 'true';

    return (
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center p-4">
            <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
                {loading ? (
                    <>
                        <Loader2 className="h-16 w-16 text-higeia-dark animate-spin mx-auto mb-4" />
                        <h1 className="text-2xl font-bold text-gray-900 mb-2">Processando...</h1>
                        <p className="text-gray-600">Aguarde enquanto confirmamos sua assinatura</p>
                    </>
                ) : (
                    <>
                        <div className="mb-6">
                            <CheckCircle className="h-20 w-20 text-green-500 mx-auto" />
                        </div>
                        <h1 className="text-3xl font-bold text-gray-900 mb-2">
                            {isUpgrade ? 'Plano Atualizado!' : 'Assinatura Confirmada!'}
                        </h1>
                        <p className="text-gray-600 mb-6">
                            {isUpgrade 
                                ? 'Seu plano foi atualizado com sucesso! Você já pode aproveitar todos os recursos do novo plano.'
                                : 'Sua assinatura foi ativada com sucesso. Você já pode aproveitar todos os recursos do seu plano.'
                            }
                        </p>

                        {subscription && (
                            <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
                                <div className="space-y-2">
                                    <div>
                                        <span className="text-sm text-gray-500">Plano:</span>
                                        <p className="font-semibold text-gray-900">{subscription.plano_nome}</p>
                                    </div>
                                    <div>
                                        <span className="text-sm text-gray-500">Status:</span>
                                        <p className="font-semibold text-green-600">{subscription.status}</p>
                                    </div>
                                    {subscription.data_expiracao && (
                                        <div>
                                            <span className="text-sm text-gray-500">Próxima cobrança:</span>
                                            <p className="font-semibold text-gray-900">
                                                {new Date(subscription.proxima_cobranca).toLocaleDateString('pt-BR')}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        <div className="space-y-3">
                            <PrimaryButton
                                onClick={() => navigate('/dashboard')}
                                className="w-full bg-higeia-dark hover:bg-higeia-medium"
                            >
                                Ir para Dashboard
                                <ArrowRight className="h-5 w-5 ml-2" />
                            </PrimaryButton>

                            {countdown > 0 && (
                                <p className="text-sm text-gray-500">
                                    Redirecionando automaticamente em {countdown} segundo{countdown !== 1 ? 's' : ''}...
                                </p>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default SucessoPage;

